import api, { absolute } from "./Api";
import notifier from "./Notifier";
import db from "./db";
import { topicUrl } from "./utils";
import { messageWithSequenceId } from "./notificationUtils";
import { EVENT_MESSAGE, EVENT_MESSAGE_CLEAR, EVENT_MESSAGE_DELETE } from "./events";
import nativeBridge from "./NativeBridge";
import config from "./config";
import type { NtfyDatabase } from "./db";
import type { Subscription, Notification, NativeNotification, RemoteSubscription, Reservation } from "./types";

const validNewFlag = (value: number | undefined) => value === 0 || value === 1;
const existingNewFlag = (notification: Notification | undefined, fallback: number): number =>
  validNewFlag(notification?.new) ? notification!.new! : fallback;
const notificationSequenceId = (notification: Notification | null | undefined): string | undefined =>
  notification?.sequenceId || notification?.sequence_id || notification?.id;

class SubscriptionManager {
  db: NtfyDatabase;

  constructor(dbImpl: NtfyDatabase) {
    this.db = dbImpl;
  }

  /** All subscriptions, including "new count"; this is a JOIN, see https://dexie.org/docs/API-Reference#joining */
  async all() {
    const subscriptions = await this.db.subscriptions.toArray();
    return Promise.all(
      subscriptions.map(async (s) => ({
        ...s,
        new: await this.db.notifications.where({ subscriptionId: s.id, new: 1 }).count(),
      }))
    );
  }

  async get(subscriptionId: string) {
    return this.db.subscriptions.get(subscriptionId);
  }

  async notify(subscriptionId: string, notification: Notification) {
    if (notification.event !== EVENT_MESSAGE) {
      return;
    }
    // Callers always pass a live subscriptionId; the original code assumes the row exists.
    const subscription = (await this.get(subscriptionId))!;
    if (subscription.mutedUntil > 0) {
      return;
    }
    await notifier.notify(subscription, notification);
  }

  async add(baseUrl: string, topic: string, opts: Partial<Subscription> = {}): Promise<Subscription> {
    const id = topicUrl(baseUrl, topic);

    const existingSubscription = await this.get(id);
    if (existingSubscription) {
      return existingSubscription;
    }

    const subscription: Subscription = {
      ...opts,
      id: topicUrl(baseUrl, topic),
      baseUrl,
      topic,
      mutedUntil: 0,
      last: null,
    };

    await this.db.subscriptions.put(subscription);
    nativeBridge.upsertSubscription(subscription);

    return subscription;
  }

  async syncFromRemote(remoteSubscriptions: RemoteSubscription[], remoteReservations: Reservation[] | undefined) {
    console.log(`[SubscriptionManager] Syncing subscriptions from remote`, remoteSubscriptions);

    // Add remote subscriptions
    const remoteIds = await Promise.all(
      remoteSubscriptions.map(async (remote) => {
        const reservation = remoteReservations?.find((r) => remote.base_url === config.base_url && remote.topic === r.topic) || null;

        const local = await this.add(remote.base_url, remote.topic, {
          displayName: remote.display_name, // May be undefined
          reservation, // May be null!
        });
        if (remote.display_name !== undefined && local.displayName !== remote.display_name) {
          await this.setDisplayName(local.id, remote.display_name);
        }
        // 아이콘은 서버가 기준 — 없으면 지운다(다른 기기에서 지운 것도 따라간다)
        const icon = absolute(remote.icon);
        if (local.icon !== icon) {
          await this.setIcon(local.id, icon);
        }

        return local.id;
      })
    );

    // Remove local subscriptions that do not exist remotely
    const localSubscriptions = await this.db.subscriptions.toArray();

    await Promise.all(
      localSubscriptions.map(async (local) => {
        const remoteExists = remoteIds.includes(local.id);
        if (!local.internal && !remoteExists) {
          await this.remove(local);
        }
      })
    );
  }

  async updateState(subscriptionId: string, state: string) {
    this.db.subscriptions.update(subscriptionId, { state });
  }

  async remove(subscription: Subscription) {
    await this.db.subscriptions.delete(subscription.id);
    await this.db.notifications.where({ subscriptionId: subscription.id }).delete();
    nativeBridge.removeSubscription(subscription);
  }

  async first() {
    return this.db.subscriptions.toCollection().first(); // May be undefined
  }

  async getNotifications(subscriptionId: string) {
    // This is quite awkward, but it is the recommended approach as per the Dexie docs.
    // It's actually fine, because the reading and filtering is quite fast. The rendering is what's
    // killing performance. See  https://dexie.org/docs/Collection/Collection.offset()#a-better-paging-approach

    return this.db.notifications
      .orderBy("time") // Sort by time
      .filter((n) => n.subscriptionId === subscriptionId)
      .reverse()
      .toArray();
  }

  async getAllNotifications() {
    return this.db.notifications
      .orderBy("time") // Efficient, see docs
      .reverse()
      .toArray();
  }

  /** Adds notification, or returns false if it already exists */
  async addNotification(subscriptionId: string, notification: Notification) {
    const exists = await this.db.notifications.get(notification.id);
    if (exists || notification.event === EVENT_MESSAGE_DELETE || notification.event === EVENT_MESSAGE_CLEAR) {
      return false;
    }
    try {
      // Note: Service worker (sw.js) and addNotifications() duplicates this logic,
      // so if you change it here, change it there too.

      // Add notification to database
      await this.db.notifications.add({
        ...messageWithSequenceId(notification),
        subscriptionId,
        new: 1, // New marker (used for bubble indicator); cannot be boolean; Dexie index limitation
      });

      // FIXME consider put() for double tab
      // Update subscription last message id (for ?since=... queries)
      await this.db.subscriptions.update(subscriptionId, {
        last: notification.id,
      });
    } catch (e) {
      console.error(`[SubscriptionManager] Error adding notification`, e);
    }
    return true;
  }

  async addOrReplaceNotification(subscriptionId: string, notification: Notification) {
    if (notification.event === EVENT_MESSAGE_DELETE || notification.event === EVENT_MESSAGE_CLEAR) {
      return false;
    }
    const exists = await this.db.notifications.get(notification.id);
    if (exists) {
      await this.db.subscriptions.update(subscriptionId, {
        last: notification.id,
      });
      return false;
    }
    const sequenceId = notificationSequenceId(notification);
    if (sequenceId) {
      await this.deleteNotificationBySequenceId(subscriptionId, sequenceId);
    }
    return this.addNotification(subscriptionId, notification);
  }

  /** Adds/replaces notifications, will not throw if they exist */
  async addNotifications(subscriptionId: string, notifications: Notification[]) {
    const existing = await this.db.notifications.bulkGet(notifications.map(({ id }) => id));
    const notificationsWithSubscriptionId = notifications.map((notification, index) => {
      const existingNotification = existing[index];
      return {
        ...messageWithSequenceId(notification),
        subscriptionId,
        new: existingNotification ? existingNewFlag(existingNotification, 0) : 1,
      };
    });
    // Callers only invoke this with a non-empty array (see Poller/sw).
    const lastNotificationId = notifications.at(-1)!.id;
    await this.db.notifications.bulkPut(notificationsWithSubscriptionId);
    await this.db.subscriptions.update(subscriptionId, {
      last: lastNotificationId,
    });
  }

  async importNativeNotifications(nativeNotifications: NativeNotification[] = []) {
    const incoming = nativeNotifications.filter(({ subscriptionId, notification }) => subscriptionId && notification?.id);
    if (incoming.length === 0) {
      return;
    }

    const existing = await this.db.notifications.bulkGet(incoming.map(({ notification }) => notification.id));
    const existingBySequence = await Promise.all(
      incoming.map(({ subscriptionId, notification }): Promise<Notification[]> => {
        const sequenceId = notificationSequenceId(notification);
        return sequenceId ? this.db.notifications.where({ subscriptionId, sequenceId }).toArray() : Promise.resolve([]);
      })
    );
    const notificationsWithSubscriptionId = incoming.map(({ subscriptionId, notification, new: nativeNew }, index) => {
      const existingNotification = existing[index];
      const sequenceWasRead = existingBySequence[index].some((item) => existingNewFlag(item, 1) === 0);
      let newFlag = Number(nativeNew ? 1 : 0);
      if (sequenceWasRead) {
        newFlag = 0;
      }
      if (existingNotification) {
        newFlag = existingNewFlag(existingNotification, 0);
      }
      return {
        ...messageWithSequenceId(notification),
        subscriptionId,
        new: newFlag,
      };
    });

    await this.db.notifications.bulkPut(notificationsWithSubscriptionId);

    const lastBySubscription = new Map<string | undefined, Notification>();
    notificationsWithSubscriptionId.forEach((notification) => {
      const current = lastBySubscription.get(notification.subscriptionId);
      if (!current || notification.time >= current.time) {
        lastBySubscription.set(notification.subscriptionId, notification);
      }
    });
    await Promise.all(
      Array.from(lastBySubscription.values()).map((notification) =>
        this.db.subscriptions.update(notification.subscriptionId!, {
          last: notification.id,
        })
      )
    );
  }

  async updateNotification(notification: Notification) {
    const exists = await this.db.notifications.get(notification.id);
    if (!exists) {
      return false;
    }
    try {
      await this.db.notifications.put({ ...notification });
    } catch (e) {
      console.error(`[SubscriptionManager] Error updating notification`, e);
    }
    return true;
  }

  async deleteNotification(notificationId: string) {
    const notification = await this.db.notifications.get(notificationId);
    nativeBridge.deleteNotification(notification);
    await this.db.notifications.delete(notificationId);
    await this.publishNotificationDelete(notification);
  }

  async deleteNotificationBySequenceId(subscriptionId: string, sequenceId: string) {
    nativeBridge.deleteNotificationSequence(subscriptionId, sequenceId);
    await this.db.notifications.where({ subscriptionId, sequenceId }).delete();
  }

  async deleteNotifications(subscriptionId: string) {
    const subscription = await this.get(subscriptionId);
    nativeBridge.deleteNotifications(subscription);
    await this.db.notifications.where({ subscriptionId }).delete();
  }

  async markNotificationRead(notificationId: string) {
    const notification = await this.db.notifications.get(notificationId);
    nativeBridge.markNotificationRead(notification);
    await this.db.notifications.where({ id: notificationId }).modify({ new: 0 });
    await this.publishNotificationRead(notification);
  }

  async markNotificationReadBySequenceId(subscriptionId: string, sequenceId: string) {
    nativeBridge.markNotificationSequenceRead(subscriptionId, sequenceId);
    await this.db.notifications.where({ subscriptionId, sequenceId }).modify({ new: 0 });
  }

  async markNotificationsRead(subscriptionId: string) {
    const subscription = await this.get(subscriptionId);
    const unreadNotifications = await this.db.notifications.where({ subscriptionId, new: 1 }).toArray();
    nativeBridge.markNotificationsRead(subscription);
    await this.db.notifications.where({ subscriptionId }).modify({ new: 0 });
    await this.publishNotificationsRead(subscription, unreadNotifications);
  }

  async publishNotificationRead(notification: Notification | null | undefined) {
    const sequenceId = notificationSequenceId(notification);
    if (!notification?.subscriptionId || !sequenceId) {
      return;
    }
    const subscription = await this.get(notification.subscriptionId);
    await this.publishNotificationSequenceRead(subscription, sequenceId);
  }

  async publishNotificationsRead(subscription: Subscription | null | undefined, notifications: Notification[]) {
    if (!subscription || notifications.length === 0) {
      return;
    }
    const sequenceIds = [...new Set(notifications.map(notificationSequenceId).filter(Boolean))];
    await Promise.all(sequenceIds.map((sequenceId) => this.publishNotificationSequenceRead(subscription, sequenceId)));
  }

  async publishNotificationSequenceRead(subscription: Subscription | null | undefined, sequenceId: string | undefined) {
    if (!subscription || !sequenceId) {
      return;
    }
    try {
      await api.markNotificationRead(subscription.topic, sequenceId);
    } catch (e) {
      console.error(`[SubscriptionManager] Error publishing read state for ${subscription.id}/${sequenceId}`, e);
    }
  }

  async publishNotificationDelete(notification: Notification | null | undefined) {
    const sequenceId = notificationSequenceId(notification);
    if (!notification?.subscriptionId || !sequenceId) {
      return;
    }
    const subscription = await this.get(notification.subscriptionId);
    if (!subscription) {
      return;
    }
    try {
      await api.deleteNotification(subscription.topic, sequenceId);
    } catch (e) {
      console.error(`[SubscriptionManager] Error publishing delete state for ${subscription.id}/${sequenceId}`, e);
    }
  }

  async setMutedUntil(subscriptionId: string, mutedUntil: number) {
    await this.db.subscriptions.update(subscriptionId, {
      mutedUntil,
    });
    const subscription = await this.get(subscriptionId);
    nativeBridge.updateSubscription(subscription);
  }

  async setDisplayName(subscriptionId: string, displayName: string) {
    await this.db.subscriptions.update(subscriptionId, {
      displayName,
    });
    const subscription = await this.get(subscriptionId);
    nativeBridge.updateSubscription(subscription);
  }

  async setIcon(subscriptionId: string, icon: string | undefined) {
    await this.db.subscriptions.update(subscriptionId, { icon });
    const subscription = await this.get(subscriptionId);
    nativeBridge.updateSubscription(subscription);
  }

  async setReservation(subscriptionId: string, reservation: Reservation | null) {
    await this.db.subscriptions.update(subscriptionId, {
      reservation,
    });
  }

  async update(subscriptionId: string, params: Partial<Subscription>) {
    await this.db.subscriptions.update(subscriptionId, params);
  }

  async pruneNotifications(thresholdTimestamp: number) {
    await this.db.notifications.where("time").below(thresholdTimestamp).delete();
  }
}

export default new SubscriptionManager(db());
